// internal-deps
import {
    CheckoutAction,
    GlobalHeaderState,
    SwgyHeaderKeysList,
    ProcessTransactionActionPayload,
    NEWRELIC_TABLES,
} from "@swiggy-private/checkout-core";
import { captureError } from "@swiggy-private/core-ui";
import { recordNREvent } from "@swiggy-private/checkout-core";

// constants
import { IS_SWIGGY_MONEY_FLOW, SWIGGY_MONEY_PAYMENT_AMOUNT } from "@payment-client/constants/StorageKeys";
import {
    ORDER_ID_KEY,
    PAYMENT_CHECKOUT_STATE_PAYLOAD_KEY,
    PAYMENT_CHECKOUT_STATE_KEY,
    WALLET_PAYMENT_CHECK_KEY,
    PENNY_TXN_ID_KEY,
    PENNY_TXN_STATE_KEY,
    PENNY_TXN_STATE_PAYLOAD_KEY,
} from "@payment-client/constants/StorageKeys";

// interfaces
import { ProcessOrderActionPayload } from "@payment-client/interfaces/Checkout";

// helper
import { isBusinessLineSwiggyMoney } from "./Misc";

const NR_ERROR_EVENT_NAME = NEWRELIC_TABLES.PAYMENT_PROCESS_ORDER_STATE_ERROR;
const PROCESS_ORDER_STORAGE_TIMESTAMP = "process_order_storage_timestamp";
const WEB_PAYMENTS_STORAGE_NOT_AVAILABLE = NEWRELIC_TABLES.WEB_PAYMENTS_STORAGE_NOT_AVAILABLE;
const TEN_MINS_IN_MS = 600 * 1000;

// We need hard null check on DOM storage as we have observed DOM storage will be null in webviews few a times
// Ref: https://sentry.io/organizations/bundl-technologies-pvt-ltd/issues/2737036128/events/latest/?project=5168575&query=is%3Aunresolved&statsPeriod=7d
const isSessionStorageAvailable = (): boolean => {
    if (typeof window !== "undefined" && typeof sessionStorage !== "undefined" && sessionStorage != null) {
        return true;
    }
    recordNREvent(
        {
            eventType: WEB_PAYMENTS_STORAGE_NOT_AVAILABLE,
            type: "session",
        },
        false,
    );
    return false;
};
const isLocalStorageAvailable = (): boolean => {
    if (typeof window !== "undefined" && typeof localStorage !== "undefined" && localStorage != null) {
        return true;
    }
    recordNREvent(
        {
            eventType: WEB_PAYMENTS_STORAGE_NOT_AVAILABLE,
            type: "local",
        },
        false,
    );
    return false;
};

export class Storage {
    public static getItem(key: string): string | null {
        if (isSessionStorageAvailable()) {
            const itemValueinSS = sessionStorage.getItem(key);
            if (itemValueinSS) {
                return itemValueinSS;
            }
        }

        if (isLocalStorageAvailable()) {
            const itemValueinLS = localStorage.getItem(key);
            const timeInLS = localStorage.getItem(PROCESS_ORDER_STORAGE_TIMESTAMP);
            if (timeInLS && itemValueinLS) {
                if (Date.now() < Number(timeInLS) + TEN_MINS_IN_MS) {
                    return itemValueinLS;
                } else {
                    key === ORDER_ID_KEY &&
                        recordNREvent(
                            {
                                eventType: NR_ERROR_EVENT_NAME,
                                method: "getItem",
                                error: "Data expired in local storage",
                                savedDataTimestamp: Number(timeInLS) / 1000,
                                orderId: itemValueinLS,
                                diffInSecs: (Date.now() - Number(timeInLS)) / 1000,
                            },
                            false,
                        );
                }
            }
        }

        return null;
    }
    static setItem(key: string, value: string): void {
        try {
            if (isSessionStorageAvailable()) {
                // set item in session storage
                sessionStorage.setItem(key, value);
            }

            if (isLocalStorageAvailable()) {
                // set item and timestamp in local storage
                localStorage.setItem(key, value);
                localStorage.setItem(PROCESS_ORDER_STORAGE_TIMESTAMP, String(Date.now()));
            }
        } catch (err) {
            recordNREvent({ eventType: NR_ERROR_EVENT_NAME, method: "setItem", error: err }, false);
            captureError(err);
        }
    }
    static removeItem(key: string): void {
        sessionStorage.removeItem(key);
        localStorage.removeItem(key);
    }
}

export function saveProcessOrderState(orderId: string, checkoutState: CheckoutAction): void {
    Storage.setItem(ORDER_ID_KEY, orderId);
    Storage.setItem(PAYMENT_CHECKOUT_STATE_KEY, JSON.stringify(checkoutState.type));
    Storage.setItem(PAYMENT_CHECKOUT_STATE_PAYLOAD_KEY, JSON.stringify(checkoutState.payload));
    Storage.setItem(IS_SWIGGY_MONEY_FLOW, String(isBusinessLineSwiggyMoney()));
    // key only needed in process order state for swiggy money
    if (isBusinessLineSwiggyMoney())
        Storage.setItem(SWIGGY_MONEY_PAYMENT_AMOUNT, GlobalHeaderState.getHeader(SwgyHeaderKeysList.paymentamount));
    else Storage.removeItem(SWIGGY_MONEY_PAYMENT_AMOUNT);
}

export function cleanProcessOrderState(): void {
    Storage.removeItem(ORDER_ID_KEY);
    Storage.removeItem(PAYMENT_CHECKOUT_STATE_KEY);
    Storage.removeItem(PAYMENT_CHECKOUT_STATE_PAYLOAD_KEY);
    Storage.removeItem(IS_SWIGGY_MONEY_FLOW);
    Storage.removeItem(SWIGGY_MONEY_PAYMENT_AMOUNT);
}

export function getProcessOrderState(): {
    orderId: string;
    state: ProcessOrderActionPayload;
    type: number;
    isSwiggyMoney: boolean;
    swiggyMoneyPaymentAmount: string | null;
} | null {
    const orderId = Storage.getItem(ORDER_ID_KEY);
    const type = Storage.getItem(PAYMENT_CHECKOUT_STATE_KEY);
    const state = Storage.getItem(PAYMENT_CHECKOUT_STATE_PAYLOAD_KEY);
    const isSwiggyMoney = Storage.getItem(IS_SWIGGY_MONEY_FLOW);
    const swiggyMoneyPaymentAmount = Storage.getItem(SWIGGY_MONEY_PAYMENT_AMOUNT);

    if (orderId == null || state == null || type == null || isSwiggyMoney == null) {
        return null;
    }

    try {
        return {
            orderId,
            state: JSON.parse(state),
            type: JSON.parse(type),
            isSwiggyMoney: isSwiggyMoney === "true",
            swiggyMoneyPaymentAmount,
        };
    } catch (err) {
        return null;
    }
}

export function getAddMoneySelectedWallet(): string | null {
    if (!isSessionStorageAvailable()) {
        return null;
    }

    return sessionStorage.getItem(WALLET_PAYMENT_CHECK_KEY);
}

export function saveAddMoneySelectedWallet(wallet: string): void {
    if (!isSessionStorageAvailable()) {
        return;
    }

    sessionStorage.setItem(WALLET_PAYMENT_CHECK_KEY, wallet);
}

export function removeAddMoneySelectedWallet(): void {
    if (isSessionStorageAvailable()) {
        return sessionStorage.removeItem(WALLET_PAYMENT_CHECK_KEY);
    }
}

export function saveProcessTransactionState(orderId: string, checkoutState: CheckoutAction): void {
    Storage.setItem(PENNY_TXN_ID_KEY, orderId);
    Storage.setItem(PENNY_TXN_STATE_KEY, JSON.stringify(checkoutState.type));
    Storage.setItem(PENNY_TXN_STATE_PAYLOAD_KEY, JSON.stringify(checkoutState.payload));
}

export function cleanProcessTransactionState(): void {
    Storage.removeItem(PENNY_TXN_ID_KEY);
    Storage.removeItem(PENNY_TXN_STATE_KEY);
    Storage.removeItem(PENNY_TXN_STATE_PAYLOAD_KEY);
}

export function getProcessTransactionState(): {
    orderId: string;
    state: ProcessTransactionActionPayload;
    type: number;
} | null {
    const orderId = Storage.getItem(PENNY_TXN_ID_KEY);
    const type = Storage.getItem(PENNY_TXN_STATE_KEY);
    const state = Storage.getItem(PENNY_TXN_STATE_PAYLOAD_KEY);

    if (orderId == null || state == null || type == null) {
        return null;
    }

    try {
        return { orderId, state: JSON.parse(state), type: JSON.parse(type) };
    } catch (err) {
        return null;
    }
}
